import React, { useState } from 'react';
import { Divider, HStack, useEditableMode, VStack, Box, EditableGroup, FormRow } from '@cad-arc';
import { EntityPanelFormGroup } from '../../panels/entity-panels/entity-panel-components';
import { EventPanelFormType } from '@mark43/cad-api';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { EventPanelFormPoliceFields } from './event-panel-form-police-fields';
import { EventPanelFormFireFields } from './event-panel-form-fire-fields';
import { EventPanelFormEMSFields } from './event-panel-form-ems-fields';
import { EventPanelFormClearEventFields } from './event-panel-form-clear-event-fields';
import { useEventPanelFormPrefill } from './use-event-panel-form-prefill';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { EventPanelFormLocationFields } from './event-panel-form-location-fields';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { observer } from 'mobx-react-lite';
import { globalAttributes } from '../../common/global-attributes';
import { useEventMetadataStore } from './event-metadata';
import { CrossClassLinks } from './cross-class-links';
import { EventPanelFormProQaButtons } from './event-panel-form-proqa-buttons';
import { EventPanelFormReadOnlyReportingPartyFields } from './event-panel-form-read-only-reporting-party-fields';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useEventPanelFormSideEffects } from './use-event-panel-form-side-effects';
import { EventPanelFormNewEventEntities } from './involved-person-vehicle-fields/event-panel-form-new-event-entities';
import { EventFormNarrativeInput } from '../../common/components/event-form-narrative-input';
import { useEventFormCallForServiceOptions } from '../../common/hooks/use-event-form-call-for-service-options';
import { EventFormAdditionalCallForServiceSelect } from '../../common/components/event-form-additional-call-for-service-select';
import { EventFormLabelsSelect } from '../../common/components/event-form-labels-select';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useEventPhoneCallMapSync } from './hooks/use-event-phone-call-map-sync';
import { EventPanelFormProQaSync } from './event-panel-form-proqa-cfs-sync';

export const EventPanelFormBody = observer(
    function EventPanelFormBody(): React.ReactElement | null {
        const { form: eventForm, isSubmitting } = useIntegratedForm<EventPanelFormType>();
        const {
            eventsHaveFireDAreaAndFireAgency,
            eventsHaveEmsDAreaAndEmsAgency,
            eventsHaveRiskLevel,
            proQaEnabled,
            proQaFireEnabled,
            proQaPoliceEnabled,
            proQaEnhancementsEnabled,
        } = useEnabledFeatures();
        const { eventPanelConfiguration, eventPanelOptions } = useBootstrapState();
        const [editableMode, setEditableMode] = useEditableMode('view');
        const eventId = useWorkspacePanel().linkedEntityId;
        const eventExists = !!eventId;
        React.useEffect(() => {
            if (eventExists) {
                setEditableMode('view');
            }
            // a bug in useEditableMode means we can't use it as a dependency
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventExists]);

        const eventMetadata = useEventMetadataStore().state.metadata;
        useEventPanelFormSideEffects(eventForm);
        useEventPanelFormPrefill(eventForm);

        useEventPhoneCallMapSync(eventForm);

        const callForServiceOptions = useEventFormCallForServiceOptions();

        const [currentProQaTempId, setCurrentProQaTempId] = useState<string | undefined>(undefined);

        if (!eventPanelOptions) {
            return null;
        }

        const isNewEventProQaPlacement = proQaEnhancementsEnabled && !eventId;
        const showProQaButtons = proQaEnabled || proQaFireEnabled || proQaPoliceEnabled;

        const proQaButtons = showProQaButtons ? (
            <EventPanelFormProQaButtons
                policeInstanceEnabled={proQaPoliceEnabled}
                fireInstanceEnabled={proQaFireEnabled}
                emsInstanceEnabled={proQaEnabled}
                enhancementsEnabled={proQaEnhancementsEnabled}
                isDraftEvent={!eventId}
                setCurrentTempId={setCurrentProQaTempId}
            />
        ) : null;

        return (
            <>
                <EventPanelFormProQaSync
                    eventId={eventId}
                    eventForm={eventForm}
                    eventPanelOptions={eventPanelOptions}
                    callForServiceOptions={callForServiceOptions}
                    currentProQaTempId={currentProQaTempId}
                />
                <VStack w="100%" alignItems="flex-start" spacing={4}>
                    <EditableGroup mode={editableMode}>
                        <EntityPanelFormGroup spacing={1}>
                            <EventPanelFormLocationFields form={eventForm} eventId={eventId} />
                            {!eventId ||
                            eventMetadata?.agencyTypeGlobalAttrId ===
                                globalAttributes.agencyTypeGlobal.POLICE ? (
                                <EventPanelFormPoliceFields
                                    form={eventForm}
                                    eventPanelOptions={eventPanelOptions}
                                />
                            ) : undefined}
                            {(!eventId && eventsHaveFireDAreaAndFireAgency) ||
                            eventMetadata?.agencyTypeGlobalAttrId ===
                                globalAttributes.agencyTypeGlobal.FIRE ? (
                                <EventPanelFormFireFields
                                    form={eventForm}
                                    eventPanelOptions={eventPanelOptions}
                                />
                            ) : undefined}
                            {(!eventId && eventsHaveEmsDAreaAndEmsAgency) ||
                            eventMetadata?.agencyTypeGlobalAttrId ===
                                globalAttributes.agencyTypeGlobal.EMS ? (
                                <EventPanelFormEMSFields
                                    form={eventForm}
                                    eventPanelOptions={eventPanelOptions}
                                />
                            ) : undefined}
                        </EntityPanelFormGroup>
                        <Divider />
                        {isNewEventProQaPlacement && proQaButtons}
                        <EntityPanelFormGroup>
                            <HStack justifyContent="space-between">
                                <Box w="100%">
                                    <FormSelect
                                        showRequiredIndicator
                                        control={eventForm.control}
                                        label="Call For Service"
                                        name="callForService"
                                        eventNotificationName="CFS"
                                        options={callForServiceOptions}
                                        asEditable={!!eventId}
                                    />
                                </Box>
                                <CrossClassLinks />
                            </HStack>
                            <EventFormAdditionalCallForServiceSelect />
                            <FormRow>
                                {eventsHaveRiskLevel ? (
                                    <FormSelect
                                        control={eventForm.control}
                                        label="Risk Level"
                                        name="riskLevel"
                                        eventNotificationName="RISK_LEVEL"
                                        options={eventPanelOptions.riskLevelOptions}
                                        showRequiredIndicator={true}
                                        length="sm"
                                    />
                                ) : undefined}
                                <FormSelect
                                    control={eventForm.control}
                                    label="Priority"
                                    name="priority"
                                    eventNotificationName="EVENT_PRIORITY"
                                    options={eventPanelOptions.priorityOptions}
                                    showRequiredIndicator={true}
                                    length="sm"
                                />
                                {eventMetadata?.agencyTypeGlobalAttrId ===
                                globalAttributes.agencyTypeGlobal.FIRE ? (
                                    <FormSelect
                                        control={eventForm.control}
                                        label="Alarm Level"
                                        name="alarmLevel"
                                        options={eventPanelOptions.alarmLevelOptions}
                                        eventNotificationName="ALARM_LEVEL"
                                        showRequiredIndicator={true}
                                        length="sm"
                                    />
                                ) : undefined}
                                <FormSelect
                                    control={eventForm.control}
                                    label="Origin"
                                    name="eventOrigin"
                                    options={eventPanelOptions.eventOriginOptions}
                                    showRequiredIndicator={true}
                                    length="sm"
                                />
                                <FormSelect
                                    control={eventForm.control}
                                    label="Disp"
                                    name="eventQueue"
                                    options={eventPanelOptions.eventQueueOptions}
                                    showRequiredIndicator={true}
                                    length="sm"
                                />
                            </FormRow>
                            <EventFormNarrativeInput
                                eventId={eventId}
                                showRequiredIndicator
                                mode={isSubmitting ? 'read-only' : undefined}
                            />
                            <EventFormLabelsSelect />
                            {
                                // The reporting party fields are read-only,
                                // so it makes sense to only show them when we have persisted event
                                eventId && <EventPanelFormReadOnlyReportingPartyFields />
                            }
                        </EntityPanelFormGroup>
                        {!eventId ? (
                            <>
                                <EventPanelFormNewEventEntities
                                    eventForm={eventForm}
                                    eventPanelOptions={eventPanelOptions}
                                    eventPanelConfig={eventPanelConfiguration}
                                />
                                <Divider />
                                <EventPanelFormClearEventFields
                                    form={eventForm}
                                    eventPanelOptions={eventPanelOptions}
                                />
                            </>
                        ) : undefined}
                        {!isNewEventProQaPlacement && proQaButtons}
                    </EditableGroup>
                </VStack>
            </>
        );
    }
);
